/** The House Rules editor and surfaced proposals (M5/M15). Split from the api.ts monolith 2026-08-03; the barrel re-exports. */
import { type RawError, call, get, num, post } from "./core";
import { type HouseRules } from "./onboarding";
// --- House Rules editor (M5, PRD §5.2, FR-2.5, FR-8.5) ----------------------

/**
 * The editor endpoints are owner-scoped: the door is the caller's, so no id
 * travels in the path. Everything below normalises tolerantly — the API is
 * still moving, and a missing optional field must never blank the screen.
 */

export type RulesCurrent = { door_id: string; version: number; markdown: string };

export type RulesVersionSummary = {
  version: number;
  /** Raw server label (`recipient`, `nob_proposal`, `rollback`, …). */
  author: string;
  change_note: string | null;
  created_at: string | null;
  chars: number;
};

export type DiffLine = { type: "same" | "add" | "del"; line: string };

/**
 * A pair of lines that may pull in opposite directions (R-0905-6). Advisory:
 * shown above the diff, and never a gate on Accept.
 */
export type RulesConflict = {
  existing_line: string;
  proposed_line: string;
};

/**
 * What this document cannot do, said before Accept (ruling R-0910-4). Advisory
 * like a conflict: the server computes it, the card shows it, and Accept is the
 * same plain Accept underneath.
 */
export type RulesWarning = { code: string; text: string };

export type Proposal = {
  proposal_id: string;
  diff_lines: DiffLine[];
  resulting_markdown: string;
  conflicts: RulesConflict[];
  warnings: RulesWarning[];
};

export type RulesFailureCode =
  | "UNAUTHENTICATED"
  | "DOOR_NOT_FOUND"
  | "RULES_TOO_LARGE"
  | "INPUT_REFUSED"
  | "ALREADY_RESOLVED"
  | "PROPOSAL_NOT_FOUND"
  | "RULES_VERSION_NOT_FOUND"
  | "REGULATED_CATEGORY"
  | "VALIDATION_ERROR"
  | "UNKNOWN";

/** `line` carries the offending rule when the guard names one (FR-2.4). */
export type RulesFailure = { ok: false; code: RulesFailureCode; line: string | null };

export type RulesResult<T> = ({ ok: true } & T) | RulesFailure;

/** The guard may name the offending line in any of a few shapes. Take any of them. */
function offendingLine(details: unknown): string | null {
  if (!details || typeof details !== "object") return null;
  const d = details as Record<string, unknown>;
  for (const key of ["line", "offending_line", "offending", "rule", "matched_line"]) {
    const value = d[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  const lines = d["lines"];
  if (Array.isArray(lines)) {
    const first = lines.find((l) => typeof l === "string" && l.trim());
    if (typeof first === "string") return first.trim();
  }
  return null;
}

function failure(status: number, body: RawError): RulesFailure {
  const code = body.error?.code;
  const line = offendingLine(body.error?.details);
  if (status === 401) return { ok: false, code: "UNAUTHENTICATED", line: null };
  if (status === 409) return { ok: false, code: "ALREADY_RESOLVED", line: null };
  if (code === "RULES_TOO_LARGE") return { ok: false, code: "RULES_TOO_LARGE", line: null };
  if (code === "INPUT_REFUSED") return { ok: false, code: "INPUT_REFUSED", line };
  if (code === "REGULATED_CATEGORY") return { ok: false, code: "REGULATED_CATEGORY", line: null };
  if (code === "VALIDATION_ERROR") return { ok: false, code: "VALIDATION_ERROR", line: null };
  if (code === "PROPOSAL_NOT_FOUND") return { ok: false, code: "PROPOSAL_NOT_FOUND", line: null };
  if (code === "RULES_VERSION_NOT_FOUND") {
    return { ok: false, code: "RULES_VERSION_NOT_FOUND", line: null };
  }
  if (status === 404) return { ok: false, code: "DOOR_NOT_FOUND", line: null };
  return { ok: false, code: "UNKNOWN", line: null };
}

const OFFLINE: RulesFailure = { ok: false, code: "UNKNOWN", line: null };

/** A unified diff string is the fallback when `diff_lines` is absent.
 * Exported for the chat's proposal card, which receives only the string. */
export function diffLinesFromText(diff: string): DiffLine[] {
  const lines = diff
    .replace(/\r\n/g, "\n")
    .split("\n")
    // Hunk headers are a diff tool's bookkeeping, not a rules change.
    .filter((l) => !l.startsWith("@@"));
  while (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
  return lines.map((raw) => {
    if (raw.startsWith("+")) return { type: "add", line: raw.slice(1).replace(/^ /, "") };
    if (raw.startsWith("-")) return { type: "del", line: raw.slice(1).replace(/^ /, "") };
    return { type: "same", line: raw.replace(/^ /, "") };
  });
}

function normaliseDiffLines(raw: unknown, diff: unknown): DiffLine[] {
  if (Array.isArray(raw)) {
    const out: DiffLine[] = [];
    for (const item of raw) {
      if (!item || typeof item !== "object") continue;
      const l = item as { type?: unknown; line?: unknown };
      const type = l.type === "add" || l.type === "del" ? l.type : "same";
      out.push({ type, line: typeof l.line === "string" ? l.line : "" });
    }
    if (out.length > 0) return out;
  }
  return typeof diff === "string" && diff.trim() ? diffLinesFromText(diff) : [];
}

function normaliseConflicts(raw: unknown, lines: DiffLine[]): RulesConflict[] {
  if (!Array.isArray(raw)) return [];
  const firstAdd = lines.find((l) => l.type === "add")?.line ?? "";
  const out: RulesConflict[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const c = item as { existing_line?: unknown; proposed_line?: unknown };
    const existing = typeof c.existing_line === "string" ? c.existing_line : "";
    if (!existing.trim()) continue; // a conflict with nothing to show is not one
    out.push({
      existing_line: existing,
      proposed_line:
        typeof c.proposed_line === "string" && c.proposed_line ? c.proposed_line : firstAdd,
    });
  }
  return out;
}

/** Accept and rollback both answer with the saved version, in one shape or another. */
function normaliseRules(body: unknown): HouseRules | null {
  if (!body || typeof body !== "object") return null;
  const b = body as { version?: unknown; markdown?: unknown; house_rules?: unknown };
  const nested =
    b.house_rules && typeof b.house_rules === "object"
      ? (b.house_rules as Record<string, unknown>)
      : null;
  const version =
    typeof b.version === "number"
      ? b.version
      : nested && typeof nested["version"] === "number"
        ? (nested["version"] as number)
        : null;
  const markdown =
    typeof b.markdown === "string"
      ? b.markdown
      : nested && typeof nested["markdown"] === "string"
        ? (nested["markdown"] as string)
        : null;
  if (version === null || markdown === null) return null;
  return { version, markdown };
}

export async function fetchCurrentRules(): Promise<RulesResult<{ current: RulesCurrent }>> {
  const r = await call(() => get("/api/v1/rules/current"));
  if (!r) return OFFLINE;
  if (!r.res.ok) return failure(r.res.status, r.body as RawError);
  const b = r.body as Partial<RulesCurrent>;
  return {
    ok: true,
    current: {
      door_id: typeof b.door_id === "string" ? b.door_id : "",
      version: typeof b.version === "number" ? b.version : 1,
      markdown: typeof b.markdown === "string" ? b.markdown : "",
    },
  };
}

/**
 * THE DOCUMENT THE WIZARD COMPOSED (ruling R-0910-4, amended) — what "Restore
 * default" puts on the table. A read: the caller proposes what comes back
 * exactly as a hand edit does, so the diff and the warnings render and Accept
 * is what writes the version. Nothing here saves anything.
 */
export async function fetchOriginalRules(): Promise<RulesResult<{ markdown: string }>> {
  const r = await call(() => get("/api/v1/rules/original"));
  if (!r) return OFFLINE;
  if (!r.res.ok) return failure(r.res.status, r.body as RawError);
  const markdown = (r.body as { markdown?: unknown }).markdown;
  return typeof markdown === "string" && markdown.trim() ? { ok: true, markdown } : OFFLINE;
}

export async function fetchVersions(): Promise<RulesResult<{ versions: RulesVersionSummary[] }>> {
  const r = await call(() => get("/api/v1/rules/versions"));
  if (!r) return OFFLINE;
  if (!r.res.ok) return failure(r.res.status, r.body as RawError);
  const raw = (r.body as { versions?: unknown }).versions;
  const versions: RulesVersionSummary[] = [];
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (!item || typeof item !== "object") continue;
      const v = item as Record<string, unknown>;
      if (typeof v["version"] !== "number") continue;
      versions.push({
        version: v["version"],
        author: typeof v["author"] === "string" ? v["author"] : "",
        change_note:
          typeof v["change_note"] === "string" && v["change_note"].trim() ? v["change_note"] : null,
        created_at: typeof v["created_at"] === "string" ? v["created_at"] : null,
        chars: typeof v["chars"] === "number" ? v["chars"] : 0,
      });
    }
  }
  // Newest first is the server's contract; sorting locally costs nothing and
  // means a history that arrives the other way round still reads correctly.
  versions.sort((a, b) => b.version - a.version);
  return { ok: true, versions };
}

export async function fetchRulesVersion(n: number): Promise<RulesResult<{ rules: HouseRules }>> {
  const r = await call(() => get(`/api/v1/rules/version/${n}`));
  if (!r) return OFFLINE;
  if (!r.res.ok) return failure(r.res.status, r.body as RawError);
  const rules = normaliseRules(r.body);
  return rules ? { ok: true, rules } : OFFLINE;
}

export type ProposeInput = { instruction: string } | { markdown: string };

/** Proposes and saves nothing (FR-2.5). The diff is the whole point. */
export async function proposeRules(
  input: ProposeInput,
): Promise<RulesResult<{ proposal: Proposal }>> {
  const r = await call(() => post("/api/v1/rules/propose", input));
  if (!r) return OFFLINE;
  if (!r.res.ok) return failure(r.res.status, r.body as RawError);
  const b = r.body as Record<string, unknown>;
  const lines = normaliseDiffLines(b["diff_lines"], b["diff"]);
  return {
    ok: true,
    proposal: {
      proposal_id: typeof b["proposal_id"] === "string" ? b["proposal_id"] : "",
      diff_lines: lines,
      resulting_markdown:
        typeof b["resulting_markdown"] === "string" ? b["resulting_markdown"] : "",
      conflicts: normaliseConflicts(b["conflicts"], lines),
      warnings: normaliseWarnings(b["warnings"]),
    },
  };
}

/** Strings only, and only the two fields the card reads. */
function normaliseWarnings(raw: unknown): RulesWarning[] {
  if (!Array.isArray(raw)) return [];
  const out: RulesWarning[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const w = item as Record<string, unknown>;
    if (typeof w["text"] !== "string" || w["text"].trim().length === 0) continue;
    out.push({ code: typeof w["code"] === "string" ? w["code"] : "", text: w["text"] });
  }
  return out;
}

export async function acceptProposal(
  proposalId: string,
  editedMarkdown?: string,
): Promise<RulesResult<{ rules: HouseRules }>> {
  const body =
    editedMarkdown === undefined
      ? { proposal_id: proposalId }
      : { proposal_id: proposalId, edited_markdown: editedMarkdown };
  const r = await call(() => post("/api/v1/rules/accept", body));
  if (!r) return OFFLINE;
  if (!r.res.ok) return failure(r.res.status, r.body as RawError);
  const rules = normaliseRules(r.body);
  return rules ? { ok: true, rules } : OFFLINE;
}

/** Rollback is a forward write: version N+1 carrying the older content (FR-8.6). */
export async function rollbackRules(
  toVersion: number,
): Promise<RulesResult<{ rules: HouseRules }>> {
  const r = await call(() => post("/api/v1/rules/rollback", { to_version: toVersion }));
  if (!r) return OFFLINE;
  if (!r.res.ok) return failure(r.res.status, r.body as RawError);
  const rules = normaliseRules(r.body);
  return rules ? { ok: true, rules } : OFFLINE;
}

// --- surfaced rule proposals (M15, PRD FR-8.3) ------------------------------

/**
 * The feedback loop's own suggestions (FR-8.3): batched signals turned into a
 * proposed line and surfaced on the rules home for the recipient to Add, Edit
 * or Dismiss. Nothing here is ever a silent write — a proposal is an offer, and
 * the recipient stays in control of their own door. Everything below normalises
 * as tolerantly as the M5 editor above: a missing optional field, or a diff
 * carried only as a string, must never blank a card.
 */
export type ProposalTrigger = "action_pattern" | "explicit" | "consolidation" | "contradiction";

const PROPOSAL_TRIGGERS: readonly ProposalTrigger[] = [
  "action_pattern",
  "explicit",
  "consolidation",
  "contradiction",
];

export type SurfacedProposal = {
  id: string;
  trigger_kind: ProposalTrigger;
  signal_count: number;
  proposed_markdown: string;
  /** Normalised from `diff_lines`, or parsed from `diff_preview` when that is all there is. */
  diff_lines: DiffLine[];
  conflicts: RulesConflict[];
  /** The evidence in a sentence: "4 recruiter knocks marked not helpful". */
  evidence_summary: string;
  created_at: string | null;
  /** Raw server status (`surfaced`, `queued`, …); a settled one never reaches a card. */
  status: string;
};

/** An unknown trigger is read as an observed pattern — the mildest framing, never guessed up. */
function triggerOf(value: unknown): ProposalTrigger {
  return typeof value === "string" && (PROPOSAL_TRIGGERS as readonly string[]).includes(value)
    ? (value as ProposalTrigger)
    : "action_pattern";
}

function normaliseProposal(raw: unknown): SurfacedProposal | null {
  if (!raw || typeof raw !== "object") return null;
  const b = raw as Record<string, unknown>;
  const id = typeof b["id"] === "string" ? b["id"] : "";
  if (!id) return null; // a proposal with no id is one nothing can act on
  const lines = normaliseDiffLines(b["diff_lines"], b["diff_preview"] ?? b["diff"]);
  return {
    id,
    trigger_kind: triggerOf(b["trigger_kind"]),
    signal_count: num(b["signal_count"]),
    proposed_markdown: typeof b["proposed_markdown"] === "string" ? b["proposed_markdown"] : "",
    diff_lines: lines,
    conflicts: normaliseConflicts(b["conflicts"], lines),
    evidence_summary: typeof b["evidence_summary"] === "string" ? b["evidence_summary"].trim() : "",
    created_at: typeof b["created_at"] === "string" ? b["created_at"] : null,
    status: typeof b["status"] === "string" && b["status"] ? b["status"] : "surfaced",
  };
}

/** A settled proposal has left the queue and must never reappear on the home. */
const GONE = new Set(["dismissed", "accepted", "edited", "superseded"]);

export async function fetchProposals(): Promise<RulesResult<{ proposals: SurfacedProposal[] }>> {
  const r = await call(() => get("/api/v1/rules/proposals"));
  if (!r) return OFFLINE;
  if (!r.res.ok) return failure(r.res.status, r.body as RawError);
  const raw = (r.body as { proposals?: unknown }).proposals;
  const proposals: SurfacedProposal[] = [];
  if (Array.isArray(raw)) {
    for (const item of raw) {
      const p = normaliseProposal(item);
      if (p && !GONE.has(p.status)) proposals.push(p);
    }
  }
  return { ok: true, proposals };
}

/**
 * Accepts a surfaced proposal (FR-8.3). Its own endpoint, distinct from the M5
 * `acceptProposal` above: this settles a queued suggestion by id, where the M5
 * call accepts an interactive propose. `editedMarkdown` carries the recipient's
 * edits, when they took the proposal through the editor first.
 */
export async function acceptSurfacedProposal(
  id: string,
  editedMarkdown?: string,
): Promise<RulesResult<{ rules: HouseRules }>> {
  const body = editedMarkdown === undefined ? {} : { edited_markdown: editedMarkdown };
  const r = await call(() =>
    post(`/api/v1/rules/proposals/${encodeURIComponent(id)}/accept`, body),
  );
  if (!r) return OFFLINE;
  if (!r.res.ok) return failure(r.res.status, r.body as RawError);
  const rules = normaliseRules(r.body);
  return rules ? { ok: true, rules } : OFFLINE;
}

export async function dismissProposal(id: string): Promise<RulesResult<{ dismissed: true }>> {
  const r = await call(() => post(`/api/v1/rules/proposals/${encodeURIComponent(id)}/dismiss`, {}));
  if (!r) return OFFLINE;
  if (!r.res.ok) return failure(r.res.status, r.body as RawError);
  return { ok: true, dismissed: true };
}
